import { createContext, useContext } from 'react';

/**
 * Coordination between AppShell and whatever it renders as `rightSidebar`.
 *
 * On desktop the right sidebar is a persistent column that owns its own
 * expanded/collapsed state (Alice's rail, for example). On a phone there is no
 * room for a persistent column, so the shell promotes the panel to a
 * full-screen overlay it opens and closes itself.
 *
 * A panel reads this context to answer two questions:
 *   - `inMobileOverlay` — am I currently the full-screen overlay? If so, skip
 *     the collapsed rail and any fixed pixel width; the shell sizes me.
 *   - `close` — the shell's dismiss handler, so a panel's own collapse control
 *     closes the overlay instead of collapsing to a rail nobody can see.
 *
 * Panels rendered outside an AppShell get the inert default and behave exactly
 * as they did before.
 */
export interface ShellPanelState {
  inMobileOverlay: boolean;
  close: () => void;
}

const INERT: ShellPanelState = { inMobileOverlay: false, close: () => undefined };

const ShellPanelContext = createContext<ShellPanelState>(INERT);

export const ShellPanelProvider = ShellPanelContext.Provider;

export function useShellPanel(): ShellPanelState {
  return useContext(ShellPanelContext);
}
